import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Grid,
  Typography,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Fab,
  Autocomplete,
  Chip
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSkillSheetMainStepper } from '../context/SkillSheetMainStepperContext';
import { useRestoreChildOptions } from '../hooks/useRestoreChildOptions';

export default function LanguageFrameworkForm({
  title,
  fieldName,
  masterDataKey,
  frameworkType,
  stepIndex,
  isEdit = false
}) {
  // コンテキストの取得を条件分岐で行う
  let stepperContext;

  if (isEdit) {
    try {
      stepperContext = useSkillSheetMainStepper();
    } catch (error) {
      console.error('編集用コンテキストの取得に失敗しました:', error);
      throw error;
    }
  } else {
    try {
      stepperContext = useSkillSheetMainStepper();
    } catch (error) {
      console.error('作成用コンテキストの取得に失敗しました:', error);
      throw error;
    }
  }

  const {
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
    fetchMasterData,
    fetchFrameworkData,
    masterData,
    validateStep
  } = stepperContext;

  // バリデーション表示の状態
  const [showValidation, setShowValidation] = useState(false);
  // 削除確認ダイアログの状態
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, index: null });
  // 新しく追加された要素へのスクロール用ref
  const formRefs = useRef([]);
  // 各言語のフレームワーク選択肢を保持
  const [frameworkOptions, setFrameworkOptions] = useState({});
  // 各言語のフレームワーク入力値を保持
  const [frameworkInputValues, setFrameworkInputValues] = useState({});

  // フォームの値を監視
  const languages = watch(fieldName) || [{ language_name: '', language_id: '', fw_library_name: '', fw_library_id: '' }];

  // 既に選択済みの言語を取得する関数
  const getSelectedLanguages = useCallback(() => {
    return languages
      .map(lang => lang.language_name)
      .filter(name => name && name.trim() !== '');
  }, [languages]);

  // 既に選択済みのフレームワークを取得する関数
  const getSelectedFrameworks = useCallback((currentIndex) => {
    const allFrameworks = [];
    languages.forEach((lang, index) => {
      if (index !== currentIndex && lang.fw_library_name) {
        const frameworks = lang.fw_library_name.split(', ').filter(f => f.trim() !== '');
        allFrameworks.push(...frameworks);
      }
    });
    return allFrameworks;
  }, [languages]);

  // 言語の選択肢をフィルタリングする関数
  const getFilteredLanguageOptions = useCallback((currentIndex) => {
    const selectedLanguages = getSelectedLanguages();
    const currentLanguage = languages[currentIndex]?.language_name;

    return (masterData[masterDataKey] || []).filter(lang => {
      const languageName = typeof lang === 'string' ? lang : lang.name;
      return !selectedLanguages.includes(languageName) || languageName === currentLanguage;
    });
  }, [masterData, masterDataKey, getSelectedLanguages, languages]);

  // フレームワークの選択肢をフィルタリングする関数
  const getFilteredFrameworkOptions = useCallback((currentIndex) => {
    const selectedFrameworks = getSelectedFrameworks(currentIndex); // 他のフォームで選択済み
    const currentFrameworks = languages[currentIndex]?.fw_library_name 
      ? languages[currentIndex].fw_library_name.split(', ').filter(f => f.trim() !== '')
      : []; // 現在のフォームで選択済み

    // 全ての選択済みフレームワークを結合
    const allSelectedFrameworks = [...selectedFrameworks, ...currentFrameworks];

    return (frameworkOptions[languages[currentIndex]?.language_name] || []).filter(fw => {
      const frameworkName = typeof fw === 'string' ? fw : fw.name;
      return !allSelectedFrameworks.includes(frameworkName);
    });
  }, [frameworkOptions, getSelectedFrameworks, languages]);

  // マスタデータを取得
  useEffect(() => {
    fetchMasterData(masterDataKey);
  }, [fetchMasterData, masterDataKey]);

  // ページ遷移などで再マウントされた際、既に言語が選択済みの行のフレームワーク候補を復元する
  useRestoreChildOptions(
    languages,
    (language) => language.language_name,
    frameworkOptions,
    setFrameworkOptions,
    (name) => fetchFrameworkData(name, frameworkType),
    masterData[masterDataKey] || [],
  );

  // バリデーション関数
  const validateForm = () => {
    let hasError = false;
    clearErrors();

    // languagesのバリデーション
    languages.forEach((language, index) => {
      const hasLanguageName = language.language_name && language.language_name !== '';
      const hasFrameworkName = language.fw_library_name && language.fw_library_name !== '';

      // フレームワークが入力されている場合のみ、言語も必須にする
      // 言語のみの場合はバリデーションしない
      if (hasFrameworkName && !hasLanguageName) {
        setError(`${fieldName}.${index}.language_name`, {
          type: 'required',
          message: '言語を入力してください'
        });
        hasError = true;
      }
    });

    if (hasError) {
      setShowValidation(true);
      // エラーがある場合、最初のエラーフィールドまでスクロール
      setTimeout(() => {
        const firstErrorField = document.querySelector('[data-error="true"]');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else {
      setShowValidation(false);
    }

    return !hasError;
  };

  // バリデーションエラーが表示された時の処理
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setShowValidation(true);
      // エラーがある場合、最初のエラーフィールドまでスクロール
      setTimeout(() => {
        const firstErrorField = document.querySelector('[data-error="true"]');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else {
      setShowValidation(false);
    }
  }, [errors]);

  // フォームバリデーションを外部から呼び出せるように登録
  useEffect(() => {
    if (validateStep) {
      validateStep(stepIndex, validateForm);
    }
  }, [validateStep, languages, stepIndex]);

  // 言語選択の変更ハンドラ
  const handleLanguageChange = async (index, selectedLanguage) => {
    const newLanguages = [...languages];

    if (typeof selectedLanguage === 'string') {
      // 手動入力の場合
      newLanguages[index] = {
        ...newLanguages[index],
        language_name: selectedLanguage,
        language_id: '',
        // 言語が変更されたらフレームワークをクリア
        fw_library_name: '',
        fw_library_id: ''
      };
    } else if (selectedLanguage && selectedLanguage.name) {
      // オートコンプリートから選択した場合
      newLanguages[index] = {
        ...newLanguages[index],
        language_name: selectedLanguage.name,
        language_id: selectedLanguage.id || '',
        // 言語が変更されたらフレームワークをクリア
        fw_library_name: '',
        fw_library_id: ''
      };

      // 選択した言語に基づいてフレームワークの選択肢を取得（言語名をキーに保存）
      try {
        const frameworkData = await fetchFrameworkData(selectedLanguage.name, frameworkType);
        setFrameworkOptions(prev => ({
          ...prev,
          [selectedLanguage.name]: frameworkData
        }));
      } catch (error) {
        console.error('Error fetching framework options:', error);
      }
    } else if (selectedLanguage === null || selectedLanguage === '') {
      // クリアされた場合
      newLanguages[index] = {
        ...newLanguages[index],
        language_name: '',
        language_id: '',
        fw_library_name: '',
        fw_library_id: ''
      };
      // フレームワークの候補は言語名をキーに保持しているため、
      // 言語名が空になれば getFilteredFrameworkOptions 側で自然に空扱いになる
    }

    setValue(fieldName, newLanguages, { shouldDirty: true, shouldTouch: true });

    // フレームワークの入力値もクリア
    setFrameworkInputValues(prev => ({
      ...prev,
      [index]: ''
    }));

    // 変更時に該当フィールドのエラーをクリア
    const fieldErrors = errors?.[fieldName];
    if (fieldErrors && fieldErrors[index]) {
      if (fieldErrors[index].language_name) {
        clearErrors(`${fieldName}.${index}.language_name`);
      }
      if (fieldErrors[index].fw_library_name) {
        clearErrors(`${fieldName}.${index}.fw_library_name`);
      }
    }
  };

  // フレームワーク選択の変更ハンドラ
  const handleFrameworkChange = (index, newValue) => {
    const newLanguages = [...languages];

    // 選択されたフレームワークを文字列に変換
    const frameworkNames = newValue.map(item =>
      typeof item === 'string' ? item : item.name || ''
    ).join(', ');

    newLanguages[index] = {
      ...newLanguages[index],
      fw_library_name: frameworkNames
    };
    setValue(fieldName, newLanguages, { shouldDirty: true, shouldTouch: true });

    // 変更時に該当フィールドのエラーをクリア
    const fieldErrors = errors?.[fieldName];
    if (fieldErrors && fieldErrors[index] && fieldErrors[index].fw_library_name) {
      clearErrors(`${fieldName}.${index}.fw_library_name`);
    }
  };

  // フレームワーク入力値変更ハンドラ
  const handleFrameworkInputChange = (index, newInputValue) => {
    setFrameworkInputValues(prev => ({
      ...prev,
      [index]: newInputValue
    }));
  };

  // フレームワークの手動入力確定
  const handleFrameworkBlur = (index) => {
    const inputValue = frameworkInputValues[index];
    if (inputValue && inputValue.trim() !== '') {
      const currentFrameworks = languages[index].fw_library_name
        ? languages[index].fw_library_name.split(', ').filter(f => f.trim() !== '')
        : [];

      const trimmedInput = inputValue.trim();
      if (!currentFrameworks.includes(trimmedInput)) {
        const newFrameworks = [...currentFrameworks, trimmedInput];
        const newLanguages = [...languages];
        newLanguages[index] = {
          ...newLanguages[index],
          fw_library_name: newFrameworks.join(', ')
        };
        setValue(fieldName, newLanguages, { shouldDirty: true, shouldTouch: true });
      }

      // 入力値をクリア
      setFrameworkInputValues(prev => ({
        ...prev,
        [index]: ''
      }));
    }
  };

  // 要素を追加
  const handleAdd = () => {
    const newLanguages = [...languages, { language_name: '', language_id: '', fw_library_name: '', fw_library_id: '' }];
    setValue(fieldName, newLanguages, { shouldDirty: true, shouldTouch: true });

    // 新しい要素にスクロール
    setTimeout(() => {
      const newIndex = newLanguages.length - 1;
      if (formRefs.current[newIndex]) {
        formRefs.current[newIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // 削除前の確認
  const handleDeleteConfirm = (index) => {
    const language = languages[index];
    const hasContent = (language.language_name && language.language_name !== '') ||
                      (language.fw_library_name && language.fw_library_name !== '');

    if (hasContent) {
      setDeleteConfirmDialog({ open: true, index });
    } else {
      handleDelete(index);
    }
  };

  // 要素を削除
  const handleDelete = (index) => {
    const newLanguages = languages.filter((_, i) => i !== index);
    setValue(
      fieldName, 
      newLanguages.length > 0 ? newLanguages : [],
      { shouldDirty: true, shouldTouch: true }
    );
    setDeleteConfirmDialog({ open: false, index: null });

    // フレームワークの候補は言語名をキーに保持しているため、行を削除しても消さなくてよい
    // （番号ではなく中身に紐づくので、削除で他の行の候補がズレることもない）
    setFrameworkInputValues(prev => {
      const newValues = { ...prev };
      delete newValues[index];
      return newValues;
    });

    // 削除に関連するエラーをクリア
    const languageNameErrorKey = `${fieldName}.${index}.language_name`;
    const frameworkNameErrorKey = `${fieldName}.${index}.fw_library_name`;
    clearErrors(languageNameErrorKey);
    clearErrors(frameworkNameErrorKey);
  };

  // バリデーションエラーの取得
  const getFieldError = (index, fieldNameSuffix) => {
    const fieldErrors = errors?.[fieldName];
    if (showValidation && fieldErrors && fieldErrors[index] && fieldErrors[index][fieldNameSuffix]) {
      return fieldErrors[index][fieldNameSuffix];
    }
    return null;
  };

  // バリデーションエラーメッセージの生成
  const validationErrors = [];
  if (showValidation && errors?.[fieldName]) {
    languages.forEach((_, index) => {
      const languageNameError = getFieldError(index, 'language_name');
      const frameworkNameError = getFieldError(index, 'fw_library_name');
      if (languageNameError) validationErrors.push(languageNameError.message);
      if (frameworkNameError) validationErrors.push(frameworkNameError.message);
    });
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ marginBottom: '24px' }}>
        {title}
      </Typography>

      {/* バリデーションエラーメッセージの表示 */}
      {showValidation && validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </Alert>
      )}

      {/* 言語項目の一覧 */}
      {languages.length > 0 ? (
        languages.map((language, index) => {
          const languageNameError = getFieldError(index, 'language_name');
          const frameworkNameError = getFieldError(index, 'fw_library_name');

          return (
            <Box
              key={index}
              ref={el => formRefs.current[index] = el}
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                p: 3,
                mb: 3,
                position: 'relative'
              }}
              data-error={!!(languageNameError || frameworkNameError)}
            >
              {/* 削除ボタン */}
              <IconButton
                onClick={() => handleDeleteConfirm(index)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: '#dc2626',
                  '&:hover': {
                    backgroundColor: '#fef2f2',
                  }
                }}
              >
                <DeleteIcon />
              </IconButton>

              <Grid container spacing={3}>
                {/* 言語 */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography sx={{ mb: 1 }}>言語</Typography>
                    <Autocomplete
                      freeSolo
                      options={getFilteredLanguageOptions(index)}
                      getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
                      value={language.language_name || ''}
                      onChange={(event, newValue) => handleLanguageChange(index, newValue)}
                      onInputChange={(event, newInputValue) => {
                        // 手動入力時の値を即座に反映
                        if (event && event.type === 'change') {
                          handleLanguageChange(index, newInputValue);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          placeholder="言語を選択または入力してください"
                          variant="outlined"
                          error={!!languageNameError}
                          helperText={languageNameError?.message}
                          onBlur={(event) => {
                            // フォーカスアウト時に手動入力値を確定
                            const inputValue = event.target.value;
                            if (inputValue && inputValue !== language.language_name) {
                              handleLanguageChange(index, inputValue);
                            }
                          }}
                          sx={{
                            '& input:focus': {
                              boxShadow: 'none',
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                </Grid>

                {/* フレームワーク */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography sx={{ mb: 1 }}>フレームワーク・ライブラリー（複数選択可）</Typography>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={getFilteredFrameworkOptions(index)}
                      getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
                      value={language.fw_library_name ? language.fw_library_name.split(', ').filter(f => f.trim() !== '') : []}
                      inputValue={frameworkInputValues[index] || ''}
                      onChange={(event, newValue) => handleFrameworkChange(index, newValue)}
                      onInputChange={(event, newInputValue) => handleFrameworkInputChange(index, newInputValue)}
                      renderTags={(value, getTagProps) =>
                        value.map((option, tagIndex) => (
                          <Chip
                            variant="filled"
                            label={option}
                            {...getTagProps({ index: tagIndex })}
                            key={tagIndex}
                            sx={{
                              backgroundColor: '#4F46E5',
                              color: 'white',
                              '& .MuiChip-deleteIcon': {
                                color: 'white',
                                '&:hover': {
                                  color: '#f0f0f0'
                                }
                              }
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          placeholder="フレームワーク・ライブラリーを選択または入力してください"
                          variant="outlined"
                          error={!!frameworkNameError}
                          helperText={frameworkNameError?.message}
                          onBlur={() => handleFrameworkBlur(index)}
                          sx={{
                            '& input:focus': {
                              boxShadow: 'none',
                            },
                          }}
                        />
                      )}
                      sx={{
                        '& .MuiAutocomplete-tag': {
                          margin: '2px',
                        },
                        '& .MuiAutocomplete-inputRoot': {
                          minHeight: '56px',
                          padding: '8px 12px',
                        },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          );
        })
      ) : (
        // すべてのフォームが削除された場合のメッセージ
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            {title}の項目を追加してください
          </Typography>
        </Box>
      )}

      {/* 追加ボタン */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Fab
          color="primary"
          onClick={handleAdd}
          sx={{
            bgcolor: '#4F46E5',
            '&:hover': {
              bgcolor: '#4338CA',
            }
          }}
        >
          <AddIcon />
        </Fab>
      </Box>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, index: null })}
      >
        <DialogTitle>削除の確認</DialogTitle>
        <DialogContent>
          <Typography>
            本当に削除しますか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmDialog({ open: false, index: null })}
            color="primary"
          >
            キャンセル
          </Button>
          <Button
            onClick={() => handleDelete(deleteConfirmDialog.index)}
            color="error"
            variant="contained"
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
